import { baseApi } from "../../../core/data/rtk/baseQuery";
import { ApiResponseType } from "../../../core/data/rtk/apiResponseType";

export interface StarterKitInventory {
    id: number;
    center_id?: number;
    course_id: number;
    course?: {
        id: number;
        name: string;
    };
    is_main_center_stock: boolean;
    total_stock: number;
    available_stock: number;
    center?: {
        id: number;
        name: string;
    };
}

export interface InventoryPayload {
    center_id?: number;
    course_id: number;
    is_main_center_stock?: boolean;
    total_stock: number;
    available_stock: number;
}

export interface TransactionPayload {
    type: 'assigned_to_center' | 'assigned_to_student';
    quantity: number;
    course_id: number;
    from_main_center?: boolean;
    from_center_id?: number;
    to_center_id?: number;
    student_id?: number;
}

export const starterKitApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        // Get All Inventories
        getInventories: builder.query<ApiResponseType<{ items: StarterKitInventory[], totalItems: number }>, any>({
            query: (params) => ({
                url: "starter-kit-inventories/get-all",
                method: "GET",
                params
            }),
            providesTags: ["StarterKitInventory"],
        }),

        // Get All Transactions
        getTransactions: builder.query<ApiResponseType<{ items: any[], totalItems: number }>, any>({
            query: (params) => ({
                url: "starter-kit-transactions/get-all",
                method: "GET",
                params
            }),
            providesTags: ["StarterKitTransaction"],
        }),

        // Save/Create Inventory
        saveInventory: builder.mutation<ApiResponseType<{ inventory: StarterKitInventory }>, InventoryPayload>({
            query: (payload) => ({
                url: "starter-kit-inventories/main-center/save",
                method: "POST",
                body: payload,
            }),
            invalidatesTags: ["StarterKitInventory"],
        }),

        // Save/Create Transaction
        saveTransaction: builder.mutation<ApiResponseType<any>, TransactionPayload>({
            query: (payload) => ({
                url: "starter-kit-transactions/save",
                method: "POST",
                body: payload,
            }),
            invalidatesTags: ["StarterKitTransaction", "StarterKitInventory", "Students", "Centers"],
        }),

        // Add Stock to Main Center
        addMainCenterStock: builder.mutation<ApiResponseType<any>, { quantity: number, course_id: number }>({
            query: (payload) => ({
                url: "starter-kit-inventories/add-main-center-stock",
                method: "POST",
                body: payload,
            }),
            invalidatesTags: ["StarterKitInventory", "StarterKitTransaction", "Centers"],
        }),

        // Update Main Center Inventory (Override)
        updateMainCenterStock: builder.mutation<ApiResponseType<any>, { total_stock: number, available_stock: number, course_id: number }>({
            query: (payload) => ({
                url: "starter-kit-inventories/update-main-center-stock",
                method: "PUT",
                body: payload,
            }),
            invalidatesTags: ["StarterKitInventory", "StarterKitTransaction", "Centers"],
        }),

        // Return Stock to Main Center
        returnToMainCenter: builder.mutation<ApiResponseType<any>, { center_id: number, quantity: number, course_id: number }>({
            query: (payload) => ({
                url: "starter-kit-transactions/return-to-main-center",
                method: "POST",
                body: payload,
            }),
            invalidatesTags: ["StarterKitInventory", "StarterKitTransaction", "Centers"],
        }),
    }),
});

export const { 
    useGetInventoriesQuery,
    useGetTransactionsQuery,
    useSaveInventoryMutation, 
    useSaveTransactionMutation,
    useAddMainCenterStockMutation,
    useUpdateMainCenterStockMutation,
    useReturnToMainCenterMutation
} = starterKitApi;
